import { CrmTaskStatus } from '@plato/data/prisma';
import { SearchEntity } from '@plato/search/search-types';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { CollapsibleText } from '@plato/ui-legacy/components/ui/collapsible-text';
import { cn } from '@plato/ui/util/classes';
import { ArrowUpRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import type { TaskCompletedSource } from '../../analytics/types/analytics-events.ts';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { useSearchEntityNavigation } from '../../shared/hooks/use-search-entity.ts';
import { TaskUpdateCheckbox } from '../../tasks/components/task-overview-table/task-update-checkbox.tsx';

type OpenTaskTimelineBodyProps = {
    task: CrmTaskDTO;
    /** This body renders in the customer feed, the global feed and the dashboard, so each host must say where it is. */
    source: TaskCompletedSource;
};

export function OpenTaskTimelineBody({ task, source }: OpenTaskTimelineBodyProps) {
    const { t } = useTranslation();
    const getEntityTypeLabel = (entity: string): string => {
        switch (entity) {
            case SearchEntity.Customer:
                return t('customer');
            case SearchEntity.Product:
                return t('product');
            case SearchEntity.SalesOrder:
                return t('sales_order');
            case SearchEntity.SalesQuote:
                return t('sales_quote');
            default:
                return entity;
        }
    };
    const { navigateToEntity } = useSearchEntityNavigation();

    const { description, descriptionRich, status, links } = task;

    // RETAIN-131: only render links to searchable entities (customer is hidden in the
    // customer feed, and non-searchable link types like AI insights aren't navigable).
    const searchEntityTypes = Object.values(SearchEntity) as string[];
    const searchableLinks = links.filter(
        (link) => link.modelType !== SearchEntity.Customer && searchEntityTypes.includes(link.modelType),
    );

    return (
        <div className="flex flex-col justify-start gap-3 text-sm text-neutral-800 sm:flex-row sm:gap-4">
            <div className="flex gap-2 flex-auto items-center">
                <TaskUpdateCheckbox task={task} source={source} />
                <div className="flex-1 overflow-hidden [&_p]:mb-0">
                    <CollapsibleText
                        key={description}
                        className={cn({
                            'line-through': status === CrmTaskStatus.COMPLETED,
                        })}
                    >
                        <RichTextViewer content={descriptionRich} />
                    </CollapsibleText>
                </div>
            </div>

            {searchableLinks.length > 0 &&
                searchableLinks.map((link) => (
                    <div className="inline-flex items-center" key={link.linkId}>
                        <Button
                            variant="link"
                            icon={ArrowUpRightIcon}
                            // className="text-neutral-600"
                            onClick={(e) => {
                                e.stopPropagation();

                                navigateToEntity({
                                    entity: link.modelType as SearchEntity,
                                    erp_id: link.modelId,
                                });
                            }}
                        >
                            {getEntityTypeLabel(link.modelType)} {link.modelId}
                        </Button>
                    </div>
                ))}
        </div>
    );
}
